import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { AuthMethod } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import useLogin from '~hooks/useLogin/useLogin';
import { inputTypes } from '~pages/Auth/types';
import { getAuthMethodsValidationSchema } from '~pages/Auth/validationSchemas';
import { emailSchema } from '~schemas/emailSchema';

interface SignInEmailFormProps {
  email?: string;
  onFetchAvailableStrategies: (args: { email: string; strategies: AuthMethod[] }) => void;
}

interface GetAuthMethodsData extends yup.InferType<
  typeof getAuthMethodsValidationSchema
> {}

const SignInEmailForm = ({ email, onFetchAvailableStrategies }: SignInEmailFormProps) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { getAuthMethods } = useLogin();

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<GetAuthMethodsData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getAuthMethodsValidationSchema),
    defaultValues: { email: email ?? '' },
  });

  const handleToggleInputFocus = useCallback(() => {
    setIsInputFocused((isFocused) => !isFocused);
  }, []);

  const onSubmit = async (data: GetAuthMethodsData) => {
    setIsLoading(true);
    try {
      const response = await getAuthMethods({ email: data.email });
      onFetchAvailableStrategies({
        email: data.email,
        strategies: response.data.data.auth_methods,
      });
    } catch {
      // Errors are handled in useLogin.
    } finally {
      setIsLoading(false);
    }
  };

  const enteredEmail = watch('email');
  const isValidEmail = useMemo(
    () => emailSchema.isValidSync(enteredEmail),
    [enteredEmail],
  );
  const shouldDisableSendMagicLinkButton = useMemo(
    () => !isValidEmail || isLoading,
    [isValidEmail, isLoading],
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <TextFormField
        control={control}
        errors={errors}
        name={inputTypes.email}
        type={inputTypes.email}
        label={`${t('form_fields.email')}`}
        inputProps={{ 'data-test-id': inputTypes.email }}
        onFocus={handleToggleInputFocus}
        onBlur={handleToggleInputFocus}
      />
      <Button
        fullWidth
        color={isInputFocused ? 'primary' : 'inherit'}
        sx={isInputFocused ? undefined : { backgroundColor: 'action.disabledBackground' }}
        variant="contained"
        type="submit"
        disabled={shouldDisableSendMagicLinkButton}
        loading={isLoading}
      >
        {t('actions.continue_with_email')}
      </Button>
    </Box>
  );
};

export default SignInEmailForm;
